import React from 'react';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import type { EditorActions } from '@atlaskit/editor-core';

import { LoadableAfterPaint } from '@confluence/loadable';

import type { SmartButtonParameters } from '../../__types__/SmartButtonConfigTypes.ts';
import { smartButtonExtensionType } from '../smartButtonConstants.ts';

const SmartButtonExtension = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SmartButtonExtension" */ './SmartButtonExtension.tsx'
			)
		).SmartButtonExtension,
});

type SmartButtonManifestArguments = {
	contentId: string;
	editorActions: EditorActions;
	openSmartButtonConfigPanel?: (localId: string) => void;
	intl: IntlShape;
	isSmartButtonAllowed: boolean;
	contentType: string;
	isTemplateEditor?: boolean;
};

type SmartButtonExtensionManifestArguments = {
	contentId: string;
	editorActions: EditorActions;
	openSmartButtonConfigPanel?: (localId: string) => void;
	isSmartButtonAllowed: boolean;
	intl: IntlShape;
	contentType: string;
	isTemplateEditor?: boolean;
};

const i18n = defineMessages({
	extensionName: {
		id: 'automation-extensions.smart-button.name',
		defaultMessage: 'Smart Button',
		description:
			'The text is shown as the display name for the Smart Button extension in the Confluence automation extensions registry, identifying this extension type to users and administrators.',
	},
	extensionDescription: {
		id: 'automation-extensions.smart-button.description',
		defaultMessage: 'Enables users to create smart buttons to run automation rules',
		description: 'Description of smart buttons extension functionality',
	},
});

// "Manifest" of the extension
// Describes extension properties and how it hooks into other parts of editor
const getSmartButtonExtensionManifest = ({
	contentId,
	editorActions,
	openSmartButtonConfigPanel,
	isSmartButtonAllowed,
	intl,
	contentType,
	isTemplateEditor = false,
}: SmartButtonExtensionManifestArguments): ExtensionManifest<SmartButtonParameters> => ({
	title: intl.formatMessage(i18n.extensionName),
	type: smartButtonExtensionType,
	key: 'smart-button',
	description: intl.formatMessage(i18n.extensionDescription),
	icons: {
		// 48 is the pixel size of the icon
		'48': () =>
			import(
				/* webpackChunkName: "loadable-automation-extensions" */
				'@confluence/icons/entry-points/AutomationIcon.ts'
			).then((mod) => mod.AutomationIcon),
	},
	// automation icon to run rule from button toolbar can be added here - see code remove for A4C-2519
	modules: {
		// This governs the / menu and + menu insertion
		// We disable extension insertion from both menus by setting this as undefined
		quickInsert:
			isSmartButtonAllowed && contentType === 'page'
				? [
						{
							key: 'quick-insert',
							keywords: ['automation'],
							action: () =>
								Promise.resolve({
									type: 'inlineExtension',
									key: 'smart-button',
									attrs: {
										extensionType: smartButtonExtensionType,
										extensionKey: 'smart-button',
										parameters: {
											extensionTitle: intl.formatMessage(i18n.extensionName),
										},
									},
								}),
						},
					]
				: undefined,
		// This is the actual node inserted into the content
		nodes: {
			default: {
				type: 'inlineExtension',
				render:
					async () =>
					({ node }) => {
						return (
							<SmartButtonExtension
								extensionNode={node}
								editorActions={editorActions}
								openSmartButtonConfigPanel={openSmartButtonConfigPanel}
								contentId={contentId}
								contentType={contentType}
								isTemplateEditor={isTemplateEditor}
							/>
						);
					},
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openSmartButtonConfigPanel) {
						openSmartButtonConfigPanel(selectedLocalId);
					}
				},
			},
		},
	},
});

export const smartButtonExtensionProvider = async ({
	contentId,
	editorActions,
	openSmartButtonConfigPanel,
	isSmartButtonAllowed,
	intl,
	contentType,
	isTemplateEditor = false,
}: SmartButtonManifestArguments): Promise<DefaultExtensionProvider<any>> =>
	new DefaultExtensionProvider<any>([
		getSmartButtonExtensionManifest({
			contentId,
			editorActions,
			openSmartButtonConfigPanel,
			isSmartButtonAllowed,
			intl,
			contentType,
			isTemplateEditor,
		}),
	]);
